import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost } from '../../lib/api-client';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '../../components/ui/table';

interface StatItem {
  id: string;
  date: string;
  deposit: number;
  depositCount: number;
  topup: number;
  topupCount: number;
  withdraw: number;
  withdrawCount: number;
  settle: number;
  settleCount: number;
  hold: number;
  feeSeller: number;
  feePlatform: number;
  fee: number;
}

interface MerchantStatsResponse {
  items: StatItem[];
  total: number;
  payinFeePercent: number;
  payoutFeePercent: number;
  merchant: { id: string; code: string; name: string };
}

function getMonthOptions(): { value: string; label: string }[] {
  const options: { value: string; label: string }[] = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString(undefined, { year: 'numeric', month: 'long' });
    options.push({ value, label });
  }
  return options;
}

function padDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function getDateRangeForMonth(yearMonth: string): { dateFrom: string; dateTo: string } {
  const [year, month] = yearMonth.split('-').map(Number);
  const lastDay = new Date(year, month, 0);
  const now = new Date();
  const isCurrentMonth = year === now.getFullYear() && month === now.getMonth() + 1;
  const endDate = isCurrentMonth ? now : lastDay;
  return {
    dateFrom: `${yearMonth}-01`,
    dateTo: padDate(endDate),
  };
}

export function MerchantStats() {
  const { t } = useTranslation();
  const { merchantId } = useParams<{ merchantId: string }>();
  const navigate = useNavigate();

  const now = new Date();
  const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const monthOptions = getMonthOptions();
  const { dateFrom, dateTo } = getDateRangeForMonth(selectedMonth);

  const { data, isLoading } = useQuery({
    queryKey: ['seller', 'merchant', merchantId, 'stats', dateFrom, dateTo],
    queryFn: () =>
      apiGet<MerchantStatsResponse>(`/seller/merchants/${merchantId}/stats`, {
        dateFrom,
        dateTo,
        pageSize: '100',
      }),
    enabled: !!merchantId,
  });

  const queryClient = useQueryClient();

  const syncMutation = useMutation({
    mutationFn: () =>
      apiPost<{ synced: number; errors: string[] }>(`/seller/sync/merchant-stats/${merchantId}`, {
        // pgway operates in GMT+7. Send month start in their TZ as UTC.
        queryParams: { date: new Date(`${selectedMonth}-01T00:00:00.000+07:00`).toISOString() },
      }),
    onSuccess: (res) => {
      if (res.ok) {
        queryClient.invalidateQueries({ queryKey: ['seller', 'merchant', merchantId, 'stats'] });
      }
    },
  });

  const stats = data?.ok ? data.data.items : [];
  const payinFeePercent = data?.ok ? data.data.payinFeePercent : 0;
  const payoutFeePercent = data?.ok ? data.data.payoutFeePercent : 0;
  const merchant = data?.ok ? data.data.merchant : null;

  const formatNum = (n: number) =>
    n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <Button variant="ghost" size="sm" onClick={() => navigate('/seller/merchants')}>
            ← {t('merchants.title')}
          </Button>
          <h1 className="mt-1 text-2xl font-bold text-gray-900 dark:text-gray-100">
            {merchant ? `${merchant.name} (${merchant.code})` : t('merchants.merchantStatsTitle')}
          </h1>
        </div>
        <Button onClick={() => syncMutation.mutate()} disabled={syncMutation.isPending} size="sm">
          {syncMutation.isPending ? t('common.loading') : t('admin.syncStats')}
        </Button>
      </div>

      {syncMutation.isSuccess && syncMutation.data?.ok && (
        <div className="rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-700 dark:border-green-800 dark:bg-green-950 dark:text-green-300">
          {t('admin.syncStats')}: {syncMutation.data.data.synced} records
          {syncMutation.data.data.errors.length > 0 &&
            ` (${syncMutation.data.data.errors.length} errors)`}
        </div>
      )}
      {syncMutation.isSuccess && !syncMutation.data?.ok && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300">
          {syncMutation.data?.error?.message}
        </div>
      )}

      {stats.length > 0 && (payinFeePercent > 0 || payoutFeePercent > 0) && (
        <div className="grid grid-cols-2 gap-4">
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-950">
            <div className="text-xs font-medium text-blue-600 dark:text-blue-400">
              {t('transactions.calcPayinFee')} ({payinFeePercent}%)
            </div>
            <div className="mt-1 text-xl font-bold text-blue-700 dark:text-blue-300">
              {formatNum(
                stats.reduce((s, r) => s + ((r.deposit + r.topup) * payinFeePercent) / 100, 0),
              )}
            </div>
            <div className="mt-0.5 text-xs text-blue-500 dark:text-blue-400">
              ({t('transactions.deposit')} + {t('transactions.topup')}) × {payinFeePercent}%
            </div>
          </div>
          <div className="rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-800 dark:bg-green-950">
            <div className="text-xs font-medium text-green-600 dark:text-green-400">
              {t('transactions.calcPayoutFee')} ({payoutFeePercent}%)
            </div>
            <div className="mt-1 text-xl font-bold text-green-700 dark:text-green-300">
              {formatNum(
                stats.reduce((s, r) => s + ((r.withdraw + r.settle) * payoutFeePercent) / 100, 0),
              )}
            </div>
            <div className="mt-0.5 text-xs text-green-500 dark:text-green-400">
              ({t('transactions.withdraw')} + {t('transactions.settle')}) × {payoutFeePercent}%
            </div>
          </div>
        </div>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>{t('merchants.merchantStatsTitle')}</CardTitle>
            <div className="flex items-center gap-3">
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="h-8 rounded-md border border-gray-200 bg-white px-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
              >
                {monthOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {dateFrom} ~ {dateTo}
              </span>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading && <div className="p-6 text-gray-400">{t('common.loading')}</div>}
          {!isLoading && stats.length === 0 && (
            <div className="p-6 text-gray-400">{t('merchants.statsEmptyHint')}</div>
          )}
          {stats.length > 0 && (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('transactions.date')}</TableHead>
                    <TableHead className="text-right">{t('transactions.deposit')}</TableHead>
                    <TableHead className="text-right">{t('transactions.depositCount')}</TableHead>
                    <TableHead className="text-right">{t('transactions.topup')}</TableHead>
                    <TableHead className="text-right">{t('transactions.topupCount')}</TableHead>
                    <TableHead className="text-right">{t('transactions.withdraw')}</TableHead>
                    <TableHead className="text-right">{t('transactions.withdrawCount')}</TableHead>
                    <TableHead className="text-right">{t('transactions.settle')}</TableHead>
                    <TableHead className="text-right">{t('transactions.hold')}</TableHead>
                    <TableHead className="text-right">{t('transactions.feeSeller')}</TableHead>
                    <TableHead className="text-right">{t('transactions.feePlatform')}</TableHead>
                    <TableHead className="text-right">{t('transactions.fee')}</TableHead>
                    <TableHead className="text-right text-blue-600 dark:text-blue-400">
                      {t('transactions.calcPayinFee')}
                    </TableHead>
                    <TableHead className="text-right text-green-600 dark:text-green-400">
                      {t('transactions.calcPayoutFee')}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(() => {
                    const filtered = stats.filter(
                      (s) => s.deposit || s.topup || s.withdraw || s.settle || s.fee,
                    );
                    const oldestDate =
                      filtered.length > 0
                        ? new Date(filtered[filtered.length - 1].date.split('T')[0])
                        : new Date();
                    const getWeekGroup = (dateStr: string) =>
                      Math.floor(
                        (new Date(dateStr).getTime() - oldestDate.getTime()) / 86400000 / 7,
                      );
                    const weekGroups: { week: number; items: StatItem[] }[] = [];
                    let curWeek: number | null = null;
                    let curItems: StatItem[] = [];
                    for (const stat of filtered) {
                      const w = getWeekGroup(stat.date);
                      if (curWeek !== null && w !== curWeek) {
                        weekGroups.push({ week: curWeek, items: curItems });
                        curItems = [];
                      }
                      curWeek = w;
                      curItems.push(stat);
                    }
                    if (curItems.length > 0 && curWeek !== null)
                      weekGroups.push({ week: curWeek, items: curItems });
                    const totalWeeks = weekGroups.length;
                    const rows: React.ReactNode[] = [];

                    weekGroups.forEach((group, idx) => {
                      const weekNum = totalWeeks - idx;
                      const dates = group.items.map((s) => s.date);
                      const dateRange = `${dates[dates.length - 1]} ~ ${dates[0]}`;

                      for (const stat of group.items) {
                        rows.push(
                          <TableRow key={stat.id}>
                            <TableCell className="whitespace-nowrap font-mono text-sm">
                              {stat.date}
                            </TableCell>
                            <TableCell className="text-right">{formatNum(stat.deposit)}</TableCell>
                            <TableCell className="text-right text-gray-500">
                              {stat.depositCount}
                            </TableCell>
                            <TableCell className="text-right">{formatNum(stat.topup)}</TableCell>
                            <TableCell className="text-right text-gray-500">
                              {stat.topupCount}
                            </TableCell>
                            <TableCell className="text-right">{formatNum(stat.withdraw)}</TableCell>
                            <TableCell className="text-right text-gray-500">
                              {stat.withdrawCount}
                            </TableCell>
                            <TableCell className="text-right">{formatNum(stat.settle)}</TableCell>
                            <TableCell className="text-right">{formatNum(stat.hold)}</TableCell>
                            <TableCell className="text-right font-medium text-primary">
                              {formatNum(stat.feeSeller)}
                            </TableCell>
                            <TableCell className="text-right">
                              {formatNum(stat.feePlatform)}
                            </TableCell>
                            <TableCell className="text-right font-medium">
                              {formatNum(stat.fee)}
                            </TableCell>
                            <TableCell className="text-right font-medium text-blue-600 dark:text-blue-400">
                              {formatNum(((stat.deposit + stat.topup) * payinFeePercent) / 100)}
                            </TableCell>
                            <TableCell className="text-right font-medium text-green-600 dark:text-green-400">
                              {formatNum(((stat.withdraw + stat.settle) * payoutFeePercent) / 100)}
                            </TableCell>
                          </TableRow>,
                        );
                      }

                      rows.push(
                        <TableRow
                          key={`week-${group.week}`}
                          className="border-t border-amber-300 bg-amber-50 font-semibold dark:border-amber-700 dark:bg-amber-950"
                        >
                          <TableCell className="whitespace-nowrap text-amber-800 dark:text-amber-200">
                            {t('transactions.weeklySummary', { week: weekNum })} ({dateRange})
                          </TableCell>
                          <TableCell className="text-right text-amber-800 dark:text-amber-200">
                            {formatNum(group.items.reduce((s, r) => s + r.deposit, 0))}
                          </TableCell>
                          <TableCell className="text-right text-amber-600 dark:text-amber-400">
                            {group.items.reduce((s, r) => s + r.depositCount, 0)}
                          </TableCell>
                          <TableCell className="text-right text-amber-800 dark:text-amber-200">
                            {formatNum(group.items.reduce((s, r) => s + r.topup, 0))}
                          </TableCell>
                          <TableCell className="text-right text-amber-600 dark:text-amber-400">
                            {group.items.reduce((s, r) => s + r.topupCount, 0)}
                          </TableCell>
                          <TableCell className="text-right text-amber-800 dark:text-amber-200">
                            {formatNum(group.items.reduce((s, r) => s + r.withdraw, 0))}
                          </TableCell>
                          <TableCell className="text-right text-amber-600 dark:text-amber-400">
                            {group.items.reduce((s, r) => s + r.withdrawCount, 0)}
                          </TableCell>
                          <TableCell className="text-right text-amber-800 dark:text-amber-200">
                            {formatNum(group.items.reduce((s, r) => s + r.settle, 0))}
                          </TableCell>
                          <TableCell className="text-right text-amber-800 dark:text-amber-200">
                            {formatNum(group.items.reduce((s, r) => s + r.hold, 0))}
                          </TableCell>
                          <TableCell className="text-right text-primary">
                            {formatNum(group.items.reduce((s, r) => s + r.feeSeller, 0))}
                          </TableCell>
                          <TableCell className="text-right text-amber-800 dark:text-amber-200">
                            {formatNum(group.items.reduce((s, r) => s + r.feePlatform, 0))}
                          </TableCell>
                          <TableCell className="text-right text-amber-800 dark:text-amber-200">
                            {formatNum(group.items.reduce((s, r) => s + r.fee, 0))}
                          </TableCell>
                          <TableCell className="text-right text-blue-600 dark:text-blue-400">
                            {formatNum(
                              group.items.reduce(
                                (s, r) => s + ((r.deposit + r.topup) * payinFeePercent) / 100,
                                0,
                              ),
                            )}
                          </TableCell>
                          <TableCell className="text-right text-green-600 dark:text-green-400">
                            {formatNum(
                              group.items.reduce(
                                (s, r) => s + ((r.withdraw + r.settle) * payoutFeePercent) / 100,
                                0,
                              ),
                            )}
                          </TableCell>
                        </TableRow>,
                      );
                    });

                    return rows;
                  })()}
                  {/* Monthly total */}
                  <TableRow className="border-t-2 border-gray-300 bg-gray-100 text-base font-bold dark:border-gray-600 dark:bg-gray-700">
                    <TableCell className="text-gray-900 dark:text-gray-100">
                      {t('transactions.total')}
                    </TableCell>
                    <TableCell className="text-right text-gray-900 dark:text-gray-100">
                      {formatNum(stats.reduce((s, r) => s + r.deposit, 0))}
                    </TableCell>
                    <TableCell className="text-right text-gray-600 dark:text-gray-300">
                      {stats.reduce((s, r) => s + r.depositCount, 0)}
                    </TableCell>
                    <TableCell className="text-right text-gray-900 dark:text-gray-100">
                      {formatNum(stats.reduce((s, r) => s + r.topup, 0))}
                    </TableCell>
                    <TableCell className="text-right text-gray-600 dark:text-gray-300">
                      {stats.reduce((s, r) => s + r.topupCount, 0)}
                    </TableCell>
                    <TableCell className="text-right text-gray-900 dark:text-gray-100">
                      {formatNum(stats.reduce((s, r) => s + r.withdraw, 0))}
                    </TableCell>
                    <TableCell className="text-right text-gray-600 dark:text-gray-300">
                      {stats.reduce((s, r) => s + r.withdrawCount, 0)}
                    </TableCell>
                    <TableCell className="text-right text-gray-900 dark:text-gray-100">
                      {formatNum(stats.reduce((s, r) => s + r.settle, 0))}
                    </TableCell>
                    <TableCell className="text-right text-gray-900 dark:text-gray-100">
                      {formatNum(stats.reduce((s, r) => s + r.hold, 0))}
                    </TableCell>
                    <TableCell className="text-right text-primary">
                      {formatNum(stats.reduce((s, r) => s + r.feeSeller, 0))}
                    </TableCell>
                    <TableCell className="text-right text-gray-900 dark:text-gray-100">
                      {formatNum(stats.reduce((s, r) => s + r.feePlatform, 0))}
                    </TableCell>
                    <TableCell className="text-right text-gray-900 dark:text-gray-100">
                      {formatNum(stats.reduce((s, r) => s + r.fee, 0))}
                    </TableCell>
                    <TableCell className="text-right text-blue-600 dark:text-blue-400">
                      {formatNum(
                        stats.reduce(
                          (s, r) => s + ((r.deposit + r.topup) * payinFeePercent) / 100,
                          0,
                        ),
                      )}
                    </TableCell>
                    <TableCell className="text-right text-green-600 dark:text-green-400">
                      {formatNum(
                        stats.reduce(
                          (s, r) => s + ((r.withdraw + r.settle) * payoutFeePercent) / 100,
                          0,
                        ),
                      )}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
